<template>
    <div class="main">
        <div class="submain-div">
            <div class="group-name-div">
                <label>Group Name</label>
                <prime-input-text v-model="groupName" class="input-div focus:border-blue-500" placeholder="e.g. Project Team" />
            </div>
        </div>
        <div class="submain-div">
            <div class="search-contacts-div" @click="handleShowContact">
                <label>Contacts</label>
                <div class="select-wrapper" :class="isShowContacts ? 'border-blue-500' : 'border-zinc-300'">
                    <span>
                        <MagnifyingGlassIcon class="icon-div" />
                    </span>
                    <p v-if="selectedContacts.length === 0">Search Contacts</p>
                    <div v-else class="drop-down-div">
                        <div v-for="(contact, index) in selectedContacts" :key="index" class="selection-div">
                            <img :src="`https://api.dicebear.com/7.x/pixel-art/svg`" alt="User Avatar" />
                            <p>
                                {{ contact.name }}
                            </p>
                            <XMarkIcon @click="removeLabel(index)" class="close-icon" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="isShowContacts" class="lists-div">
                <div v-for="(contact, index) in store.contacts" :key="index" class="contacts-list">
                    <div
                        class="contact-item"
                        :class="{
                            'flex px-2.5 py-2 items-center gap-2 flex-1 rounded-lg cursor-pointer': true,
                            'bg-zinc-50': selectedContacts.some((c) => c.id === contact.id),
                            'bg-white': !selectedContacts.some((c) => c.id === contact.id),
                        }"
                        @click="toggleSelection(contact)"
                    >
                        <PersonAvatar :name="contact.name" :person_id="contact.id" :width="24" :height="24" />
                        <p>
                            {{ contact.name }}
                        </p>

                        <CheckIcon v-if="selectedContacts.includes(contact)" class="check-icon" />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="footer-div">
        <div class="button-wrapper">
            <prime-button @click="emit('closeDialog')" variant="secondary" label="Cancel" class="p-btn-root_secondary" />
            <prime-button @click="handleCreateGroup" label="Create Group" variant="primary" :disabled="!canCreateGroup"
                :loading="loader" />
        </div>
    </div>
</template>

<script setup lang="ts">
import PersonAvatar from '~/components/main/shared/PersonAvatar.vue';
import type { Contact } from '@/types/person/contacts';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import PrimeInputText from 'primevue/inputtext';
import { useContactsTempStore } from '~/stores/person/contacts-temp';

const emit = defineEmits(['closeDialog']);

const store = useContactsTempStore();

const isShowContacts = ref(false);
const loader = ref(false);
const groupName = ref('');
const selectedContacts = ref<Contact[]>([]);

const canCreateGroup = computed(() => {
    return selectedContacts.value.length > 0 && groupName.value.trim() !== '';
});

const toggleSelection = (contact: Contact) => {
    const index = selectedContacts.value.findIndex((c) => c.id === contact.id);
    if (index === -1) {
        selectedContacts.value.push(contact);
    } else {
        selectedContacts.value.splice(index, 1);
    }
};

const removeLabel = (index: number) => {
    selectedContacts.value.splice(index, 1);
};

const handleShowContact = () => {
    if (isShowContacts.value && selectedContacts.value.length !== 0) {
        return;
    } else {
        isShowContacts.value = true;
    }
};

const handleCreateGroup = () => {
    //remove simulation
    // loader.value = true;
    // setTimeout(() => {
    //     store.createGroupChat();
    //     loader.value = false;
    //     emit('closeDialog');
    // }, 3000);
};
</script>
<style lang="scss" scoped>
.main {
    @apply flex flex-col items-start gap-4 px-6 self-stretch;
}

.submain-div {
    @apply flex flex-col items-start gap-[6px] w-full;
}

.group-name-div {
    @apply flex flex-col items-start gap-1 w-full;

    label {
        @apply text-zinc-700 font-inter text-sm font-medium leading-5;
    }

    .input-div {
        @apply flex-1 py-2 px-3 w-full;
    }
}

.search-contacts-div {
    @apply flex flex-col items-start gap-1 w-full cursor-pointer;

    label {
        @apply text-zinc-700 font-inter text-sm font-medium leading-5;
    }
}

.select-wrapper {
    @apply flex gap-[8px] min-h-10 items-center w-full border rounded-[4px] bg-white;
    @apply shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)];

    span {
        @apply px-3 text-gray-500;

        .icon-div {
            @apply h-[20px] w-[20px] text-gray-500;
        }
    }
}

.drop-down-div {
    @apply flex flex-wrap gap-[8px] items-center w-full max-h-[256px] overflow-y-auto;

    .close-icon {
        @apply w-4 h-4 text-gray-700 cursor-pointer;
    }
}

.selection-div {
    @apply flex items-center justify-center gap-[3px] p-[2px_4px_2px_5px];
    @apply rounded-md border border-gray-300 bg-white h-[24px];

    p {
        @apply text-gray-700 text-sm font-medium leading-5 text-center font-inter;
    }

    img {
        @apply w-4 h-4 rounded-full border border-zinc-300;
    }
}

.lists-div {
    @apply flex flex-col w-full max-h-[256px] p-[4px_0] items-start flex-1 rounded-lg border;
    @apply border-gray-200 bg-white shadow-lg overflow-y-auto;

    .contacts-list {
        @apply flex px-1.5 py-0.5 items-center self-stretch;
    }
}

.contact-item {
    &:hover {
        @apply bg-zinc-50;
    }

    img {
        @apply w-6 h-6 rounded-full border border-zinc-300;
    }

    p {
        @apply text-gray-700 text-sm font-medium leading-5 text-center font-inter;
    }

    .check-icon {
        @apply w-5 h-5 text-blue-600 cursor-pointer ml-auto;
    }
}

.footer-div {
    @apply flex flex-col justify-center items-end self-stretch pt-8;
}

.button-wrapper {
    @apply flex justify-end items-center gap-3 flex-[1_0_0] px-6 pb-6   w-[350px];
}
</style>
